import styles from './sessions.module.css';
import { PageHead } from '../../components/page-head';
/**
 * Student Sessions Page
 *
 * Displays the authenticated student's scheduled sessions across three tabs:
 * Upcoming (status SCHEDULED/RUNNING), Completed (FINISHED with attendance),
 * and Missed (FINISHED without attendance, plus CANCELLED). Sessions whose
 * status is RUNNING offer a Join button that opens the game-client in a new
 * tab. Status is driven entirely by the backend.
 *
 * Refresh: powered by `useStudentSessions` (TanStack Query). Picks up status
 * transitions via 60s polling and via window-focus refetch.
 */

import { Button } from '../../components/button';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { sessionShown } from '../../api/student/session-attendance';
import type { StudentSessionView } from '../../api/student/sessions/types';
import { useStudentSessions } from '../../api/student/sessions/hooks';
import { useSessionLaunch } from './use-session-launch';
import { StudentSessionCard } from './student-session-card';
import tabs from './filter-row.module.css';

type SessionTab = 'upcoming' | 'completed' | 'missed';

/** Each tab's name: its key, written whole. */
const TAB_KEY: Record<SessionTab, string> = {
  upcoming: 'student.sessions.tabs.upcoming',
  completed: 'student.sessions.tabs.completed',
  missed: 'student.sessions.tabs.missed',
};

export function StudentSessionsPage() {
  const { t } = useTranslation();
  const query = useStudentSessions();
  const { enter, launchingId, error: launchError } = useSessionLaunch();
  const [tab, setTab] = useState<SessionTab>('upcoming');

  const data = query.data;
  const loading = query.isLoading;
  const refreshing = query.isFetching && !query.isLoading;
  const error = query.isError
    ? (query.error instanceof Error ? query.error.message : t('requestErrors.load'))
    : launchError;

  const { upcoming, completed, missed } = useMemo(() => {
    const up = data?.upcoming ?? [];
    const past = data?.past ?? [];
    const completedList: StudentSessionView[] = [];
    const missedList: StudentSessionView[] = [];
    for (const s of past) {
      const standing = sessionShown(s.status, s.participated);
      if (standing === 'cancelled' || standing === 'missed') {
        missedList.push(s);
      } else {
        completedList.push(s);
      }
    }
    return { upcoming: up, completed: completedList, missed: missedList };
  }, [data]);

  const visible =
    tab === 'upcoming'  ? upcoming :
    tab === 'completed' ? completed :
                          missed;

  const tzLabel = Intl.DateTimeFormat().resolvedOptions().timeZone;

  const tabCounts: Record<SessionTab, number> = {
    upcoming: upcoming.length,
    completed: completed.length,
    missed: missed.length,
  };

  return (
    <div className="page">
      <PageHead sub={t('student.sessions.subtitle')}>{t('student.sessions.title')}</PageHead>

      <div className={tabs.filters}>
        {(['upcoming', 'completed', 'missed'] as SessionTab[]).map((f) => (
          <button
            key={f}
            type="button"
            className={`${tabs.filter} ${tab === f ? tabs.filterActive : ''}`}
            onClick={() => setTab(f)}
          >
            {t(TAB_KEY[f])}<sup>{tabCounts[f]}</sup>
          </button>
        ))}
        <button
          type="button"
          className={`${styles.sessionsRefresh}`}
          onClick={() => void query.refetch()}
          disabled={loading || refreshing}
        >
          🔄 {refreshing ? t('common.loading') : t('student.sessions.refreshButton')}
        </button>
      </div>

      {loading && <p className={`${styles.sessionsInfo}`}>{t('common.loading')}</p>}
      {error && <div className={`${styles.sessionsError}`} role="alert">{error}</div>}

      {!loading && !error && visible.length === 0 && (
        <p className={`${styles.sessionsEmpty}`}>
          {tab === 'upcoming'  ? t('student.sessions.emptyUpcoming') :
           tab === 'completed' ? t('student.sessions.emptyPast') :
                                 t('student.sessions.emptyMissed')}
        </p>
      )}

      {!loading && !error && visible.length > 0 && (
        <div>
          {visible.map((session) => (
            <StudentSessionCard
              key={session.id}
              session={session}
              variant={tab === 'upcoming' ? 'upcoming' : 'past'}
              launching={launchingId === session.id}
              onLaunch={tab === 'upcoming' ? () => void enter(session.id) : undefined}
            />
          ))}
        </div>
      )}

      <footer className={`${styles.sessionsFooter}`}>
        <span>{t('student.sessions.timezoneNote', { tz: tzLabel })}</span>
        <Button variant="gold" disabled title={t('student.sessions.calendarSoon')}>
          📅 {t('student.sessions.addToCalendar')}
        </Button>
      </footer>
    </div>
  );
}
